import React, { useState, useEffect, useRef } from 'react';
import { creatorAPI } from '../../services/api';
import { Eye, Flame, Heart, Loader, TrendingUp, Calendar, Video, ExternalLink, Tv, Music, Film, Star, Globe } from 'lucide-react';

// ——— Interactive SVG Area Chart ———
const AreaChart = ({ data, width = 700, height = 240, color = '#8B5CF6' }) => {
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  if (!data || data.length === 0) {
    return (
      <div style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#444', fontSize: '13px' }}>
        No data for this period yet
      </div>
    );
  }

  const padding = { top: 24, right: 24, bottom: 36, left: 48 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const maxVal = Math.max(...data.map(d => d.count), 1);
  const yTicks = 4;

  const xScale = (i) => padding.left + (i / Math.max(data.length - 1, 1)) * chartW;
  const yScale = (v) => padding.top + chartH - (v / maxVal) * chartH;

  const catmullRom = (pts, tension = 0.3) => {
    if (pts.length < 2) return '';
    let path = `M ${pts[0][0]},${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(0, i - 1)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(pts.length - 1, i + 2)];
      const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
      const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
      const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
      const cp2y = p2[1] - (p3[1] - p1[1]) * tension;
      path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2[0]},${p2[1]}`;
    }
    return path;
  };

  const pts = data.map((d, i) => [xScale(i), yScale(d.count)]);
  const linePath = catmullRom(pts);
  const areaPath = linePath + ` L ${xScale(data.length - 1)},${yScale(0)} L ${xScale(0)},${yScale(0)} Z`;

  const n = data.length;
  const sumX = data.reduce((s, _, i) => s + i, 0);
  const sumY = data.reduce((s, d) => s + d.count, 0);
  const sumXY = data.reduce((s, d, i) => s + i * d.count, 0);
  const sumX2 = data.reduce((s, _, i) => s + i * i, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX) || 0;
  const intercept = (sumY - slope * sumX) / n;
  const trendY0 = intercept;
  const trendY1 = slope * (n - 1) + intercept;

  const handleMouseMove = (e) => {
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (!svgRect) return;
    const mouseX = e.clientX - svgRect.left;
    const idx = Math.round(((mouseX - padding.left) / chartW) * (data.length - 1));
    const clamped = Math.max(0, Math.min(data.length - 1, idx));
    setTooltip({ idx: clamped, x: xScale(clamped), y: yScale(data[clamped].count), ...data[clamped] });
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const xLabelStep = Math.max(1, Math.floor(data.length / 6));

  return (
    <svg
      ref={svgRef}
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setTooltip(null)}
      style={{ cursor: 'crosshair' }}
    >
      {Array.from({ length: yTicks + 1 }).map((_, i) => {
        const yVal = (maxVal / yTicks) * i;
        const y = yScale(yVal);
        return (
          <g key={i}>
            <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#1A1A2E" strokeWidth="1" />
            <text x={padding.left - 10} y={y + 4} textAnchor="end" fill="#444" fontSize="10" fontFamily="inherit">
              {Math.round(yVal)}
            </text>
          </g>
        );
      })}
      <defs>
        <linearGradient id="creatorAreaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="creatorLineGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor="#C084FC" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#creatorAreaGrad)" />
      <path d={linePath} fill="none" stroke="url(#creatorLineGrad)" strokeWidth="2.5" strokeLinecap="round" />
      <line
        x1={xScale(0)} y1={yScale(trendY0)}
        x2={xScale(n - 1)} y2={yScale(trendY1)}
        stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.5"
      />
      {data.map((d, i) => (
        <circle key={i} cx={xScale(i)} cy={yScale(d.count)} r={tooltip?.idx === i ? 6 : 2.5}
          fill={tooltip?.idx === i ? color : '#0A0A0F'} stroke={color} strokeWidth="2"
          style={{ transition: 'r 0.15s' }}
        />
      ))}
      {data.map((d, i) => (
        i % xLabelStep === 0 || i === data.length - 1 ? (
          <text key={`xl-${i}`} x={xScale(i)} y={height - 8} textAnchor="middle" fill="#444" fontSize="10" fontFamily="inherit">
            {formatDate(d.date)}
          </text>
        ) : null
      ))}
      {tooltip && (
        <g>
          <line x1={tooltip.x} y1={padding.top} x2={tooltip.x} y2={padding.top + chartH} stroke={color} strokeWidth="1" strokeDasharray="3,3" opacity="0.4" />
          <rect x={tooltip.x - 60} y={tooltip.y - 42} width="120" height="34" rx="8" fill="#1A1A2E" stroke={color} strokeWidth="1" />
          <text x={tooltip.x} y={tooltip.y - 26} textAnchor="middle" fill="#FFF" fontSize="12" fontWeight="600" fontFamily="inherit">
            {tooltip.count} clicks
          </text>
          <text x={tooltip.x} y={tooltip.y - 14} textAnchor="middle" fill="#666" fontSize="10" fontFamily="inherit">
            {formatDate(tooltip.date)}
          </text>
        </g>
      )}
    </svg>
  );
};

// ——— Interactive Horizontal Bar Chart ———
const HBarChart = ({ data, maxVal }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const sourceColors = {
    explore_tab:    '#8B5CF6',
    home_featured:  '#EC4899',
    peak_moments:   '#F59E0B',
    creator_profile:'#10B981',
    search_result:  '#3B82F6',
    leaderboard:    '#6366F1',
    sidebar:        '#06B6D4',
    content_detail: '#F97316',
  };

  const sourceLabels = {
    explore_tab:    'Explore Tab',
    home_featured:  'Home Featured',
    peak_moments:   'Peak Moments',
    creator_profile:'Creator Profile',
    search_result:  'Search Results',
    leaderboard:    'Leaderboard',
    sidebar:        'Sidebar',
    content_detail: 'Content Page',
  };

  if (!data || data.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: '#444', fontSize: '13px' }}>
        No click data yet. Clicks appear as users interact with your channel.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {data.map((s, i) => {
        const pct = maxVal > 0 ? (s.count / maxVal) * 100 : 0;
        const barColor = sourceColors[s.source] || '#8B5CF6';
        const isHovered = hoveredIdx === i;

        return (
          <div
            key={s.source}
            onMouseEnter={() => setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: '14px',
              padding: '8px 12px', borderRadius: '8px',
              background: isHovered ? 'rgba(139,92,246,0.06)' : 'transparent',
              transition: 'background 0.15s',
            }}
          >
            <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: barColor }} />
            <div style={{
              width: '120px', fontSize: '13px', flexShrink: 0,
              color: isHovered ? '#E5E5E5' : '#999', fontWeight: isHovered ? '500' : '400',
              transition: 'all 0.15s',
            }}>
              {sourceLabels[s.source] || s.source}
            </div>
            <div style={{ flex: 1, height: '24px', background: '#0F0F18', borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${Math.max(pct, 1)}%`,
                background: `linear-gradient(90deg, ${barColor}, ${barColor}99)`,
                borderRadius: '6px', transition: 'width 0.5s ease',
              }} />
            </div>
            <div style={{
              minWidth: '40px', textAlign: 'right', fontSize: '14px',
              fontWeight: '600', color: isHovered ? '#FFF' : '#888',
              fontVariantNumeric: 'tabular-nums', transition: 'all 0.15s',
            }}>
              {s.count}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ——— Audience Content Interests ———
const CONTENT_TYPE_COLORS = {
  movie:  '#8B5CF6',
  tv:     '#3B82F6',
  anime:  '#F97316',
  music:  '#EC4899',
};
const CONTENT_TYPE_LABELS = { movie: 'Movie', tv: 'TV Show', anime: 'Anime', music: 'Music' };

const AudienceContentInterests = ({ byContent = [] }) => {
  const max = byContent.length > 0 ? Math.max(...byContent.map(c => c.count)) : 1;

  return (
    <div style={{ background: '#111118', borderRadius: '14px', padding: '24px', border: '1px solid #1A1A2E' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TrendingUp size={18} style={{ color: '#F97316' }} />
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#FFF' }}>What Your Audience is Watching</span>
        </div>
        <span style={{ fontSize: '12px', color: '#555' }}>Content that drove clicks to you</span>
      </div>

      {byContent.length === 0 ? (
        <div style={{ color: '#444', fontSize: '13px', textAlign: 'center', padding: '32px 20px' }}>
          Data appears as users click your channel from movie, TV, anime &amp; music pages.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {byContent.map(({ title, count, contentType }, i) => {
            const pct = max > 0 ? (count / max) * 100 : 0;
            const color = CONTENT_TYPE_COLORS[contentType] || '#6B7280';
            const typeLabel = CONTENT_TYPE_LABELS[contentType] || contentType || '—';

            return (
              <div key={title} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: i === 0 ? '#F97316' : '#444', minWidth: '20px', textAlign: 'right' }}>#{i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', color: '#E5E5E5', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '220px' }}>{title}</span>
                    <span style={{ fontSize: '10px', fontWeight: '600', color, background: `${color}20`, padding: '1px 6px', borderRadius: '4px', flexShrink: 0 }}>{typeLabel}</span>
                  </div>
                  <div style={{ height: '6px', background: '#0F0F18', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.max(pct, 2)}%`, background: `linear-gradient(90deg, ${color}, ${color}99)`, borderRadius: '3px', transition: 'width 0.5s ease' }} />
                  </div>
                </div>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#E5E5E5', minWidth: '36px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ——— Redirected Reactions Widget ———
const REDIRECT_CONFIG = {
  channel_redirect:     { label: 'Channel Visits',    icon: ExternalLink, color: '#8B5CF6' },
  video_redirect:       { label: 'Video Redirects',   icon: Film,         color: '#3B82F6' },
  peak_moment_redirect: { label: 'Peak Moment Clicks', icon: Flame,       color: '#F59E0B' },
};

const RedirectedReactionsWidget = ({ redirectsByType = [], totalRedirects = 0 }) => {
  const total = redirectsByType.reduce((s, r) => s + r.count, 0) || totalRedirects;
  const types = ['channel_redirect', 'video_redirect', 'peak_moment_redirect'];

  return (
    <div style={{
      background: '#111118', borderRadius: '14px', padding: '24px',
      border: '1px solid #1A1A2E', marginBottom: '24px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ExternalLink size={18} style={{ color: '#8B5CF6' }} />
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#FFF' }}>Redirected Reactions</span>
        </div>
        <div style={{ fontSize: '12px', color: '#555' }}>Clicks that sent users to YouTube</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        {types.map(type => {
          const cfg = REDIRECT_CONFIG[type];
          const item = redirectsByType.find(r => r.type === type);
          const count = item?.count || 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const Icon = cfg.icon;

          return (
            <div key={type} style={{
              background: `${cfg.color}0D`,
              border: `1px solid ${cfg.color}30`,
              borderRadius: '12px', padding: '18px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '7px',
                  background: `${cfg.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={14} style={{ color: cfg.color }} />
                </div>
                <span style={{ fontSize: '12px', color: '#777', fontWeight: '500' }}>{cfg.label}</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#FFF', fontVariantNumeric: 'tabular-nums' }}>
                {count.toLocaleString()}
              </div>
              <div style={{ marginTop: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '11px', color: '#555' }}>Share</span>
                  <span style={{ fontSize: '11px', color: cfg.color, fontWeight: '600' }}>{pct}%</span>
                </div>
                <div style={{ height: '4px', background: '#0F0F18', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${Math.max(pct, total > 0 ? 1 : 0)}%`,
                    background: cfg.color, borderRadius: '2px', transition: 'width 0.5s ease',
                  }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {total === 0 && (
        <div style={{ textAlign: 'center', color: '#444', fontSize: '13px', paddingTop: '16px' }}>
          No redirects tracked yet — data populates as users click out to YouTube.
        </div>
      )}
    </div>
  );
};

// ——— Media Category Breakdown ———
const CATEGORY_CONFIG = {
  'movies':            { label: 'Movies',    icon: Film,        color: '#8B5CF6' },
  'tv-shows':          { label: 'TV Shows',  icon: Tv,          color: '#3B82F6' },
  'anime':             { label: 'Anime',     icon: Star,        color: '#F97316' },
  'music-performance': { label: 'Music',     icon: Music,       color: '#EC4899' },
  'korean-drama':      { label: 'K-Drama',   icon: Tv,          color: '#10B981' },
  'general':           { label: 'General',   icon: Globe,       color: '#6B7280' },
};

const MediaCategoryBreakdown = ({ categoryBreakdown = [], channelCategories = [] }) => {
  const maxCount = categoryBreakdown.length > 0 ? Math.max(...categoryBreakdown.map(c => c.count)) : 1;

  const allCategories = [
    ...categoryBreakdown,
    ...channelCategories
      .filter(cat => !categoryBreakdown.find(c => c.category === cat))
      .map(cat => ({ category: cat, count: 0 })),
  ].sort((a, b) => b.count - a.count);

  return (
    <div style={{
      background: '#111118', borderRadius: '14px', padding: '24px',
      border: '1px solid #1A1A2E',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <Film size={18} style={{ color: '#F97316' }} />
        <span style={{ fontSize: '16px', fontWeight: '600', color: '#FFF' }}>Media Category Breakdown</span>
      </div>

      {allCategories.length === 0 ? (
        <div style={{ color: '#444', fontSize: '13px', textAlign: 'center', padding: '32px 20px' }}>
          Category data populates as users interact with your content.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {allCategories.map(({ category, count }) => {
            const cfg = CATEGORY_CONFIG[category] || { label: category, icon: Globe, color: '#6B7280' };
            const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
            const Icon = cfg.icon;

            return (
              <div key={category} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '7px', flexShrink: 0,
                  background: `${cfg.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={13} style={{ color: cfg.color }} />
                </div>
                <div style={{ width: '110px', fontSize: '13px', color: '#999', flexShrink: 0 }}>{cfg.label}</div>
                <div style={{ flex: 1, height: '22px', background: '#0F0F18', borderRadius: '5px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${count > 0 ? Math.max(pct, 2) : 0}%`,
                    background: `linear-gradient(90deg, ${cfg.color}, ${cfg.color}99)`,
                    borderRadius: '5px', transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{
                  minWidth: '48px', textAlign: 'right', fontSize: '13px', fontWeight: '600',
                  color: count > 0 ? '#E5E5E5' : '#444', fontVariantNumeric: 'tabular-nums',
                }}>
                  {count > 0 ? count.toLocaleString() : '—'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ——— Main Overview Panel ———
const CreatorOverviewPanel = ({ channelId }) => {
  const [dashboard, setDashboard] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [dashData, clickData] = await Promise.all([
          creatorAPI.getDashboard(),
          creatorAPI.getClicks(period)
        ]);
        setDashboard(dashData.dashboard);
        setClicks(clickData);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period, channelId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px', color: '#666', gap: '8px' }}>
        <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
        Loading analytics...
      </div>
    );
  }

  const maxClickCount = clicks?.bySource?.length > 0
    ? Math.max(...clicks.bySource.map(s => s.count))
    : 1;

  const redirectsByType = dashboard?.redirectsByType || [];
  const categoryBreakdown = clicks?.byCategory || dashboard?.categoryBreakdown || [];
  const channelCategories = dashboard?.channelCategories || [];
  const byContent = clicks?.byContent || dashboard?.topContentTitles || [];

  const statCards = [
    {
      icon: Eye, label: 'Total Clicks', value: clicks?.totalClicks || 0,
      color: '#8B5CF6', gradient: 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(139,92,246,0.04))',
    },
    {
      icon: Flame, label: 'Peak Moments', value: dashboard?.peakMomentsCount || 0,
      color: '#F59E0B', gradient: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(245,158,11,0.04))',
    },
    {
      icon: Heart, label: 'Favorites', value: dashboard?.channelFavorites || 0,
      color: '#EC4899', gradient: 'linear-gradient(135deg, rgba(236,72,153,0.12), rgba(236,72,153,0.04))',
    },
    {
      icon: Video, label: 'Videos Indexed', value: dashboard?.indexedVideos || 0,
      color: '#3B82F6', gradient: 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(59,130,246,0.04))',
    },
  ];

  return (
    <div>
      {/* Period selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
        <Calendar size={16} style={{ color: '#555' }} />
        <div style={{
          display: 'flex', gap: '2px', padding: '3px',
          background: '#111118', borderRadius: '8px', border: '1px solid #1A1A2E',
        }}>
          {['7d', '30d', '90d'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: '7px 18px', border: 'none', borderRadius: '6px',
                cursor: 'pointer', fontSize: '13px',
                fontWeight: period === p ? '600' : '400',
                background: period === p ? '#8B5CF6' : 'transparent',
                color: period === p ? '#FFF' : '#666',
                transition: 'all 0.15s',
              }}
            >
              {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {statCards.map((card, i) => (
          <div key={i} style={{
            background: card.gradient,
            borderRadius: '14px', padding: '20px',
            border: '1px solid rgba(255,255,255,0.04)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'default',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${card.color}15`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '8px',
                background: `${card.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <card.icon size={16} style={{ color: card.color }} />
              </div>
              <span style={{ fontSize: '12px', color: '#777', fontWeight: '500' }}>{card.label}</span>
            </div>
            <div style={{ fontSize: '30px', fontWeight: '700', color: '#FFF', fontVariantNumeric: 'tabular-nums' }}>
              {(card.value || 0).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* Redirected Reactions */}
      <RedirectedReactionsWidget
        redirectsByType={redirectsByType}
        totalRedirects={clicks?.totalRedirects || 0}
      />

      {/* Clicks Over Time - Area Chart */}
      <div style={{
        background: '#111118', borderRadius: '14px', padding: '24px',
        border: '1px solid #1A1A2E', marginBottom: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <TrendingUp size={18} style={{ color: '#8B5CF6' }} />
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#FFF' }}>Clicks Over Time</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#666' }}>
              <div style={{ width: 16, height: 2, background: 'linear-gradient(90deg, #8B5CF6, #C084FC)', borderRadius: '1px' }} />
              Clicks
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#666' }}>
              <div style={{ width: 16, height: 0, borderTop: '2px dashed #F59E0B', opacity: 0.5 }} />
              Trend
            </div>
          </div>
        </div>
        <AreaChart data={clicks?.byDay || []} color="#8B5CF6" width={700} height={240} />
      </div>

      {/* Bottom row: Clicks by Source + Media Category */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <div style={{
          background: '#111118', borderRadius: '14px', padding: '24px',
          border: '1px solid #1A1A2E',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <Eye size={18} style={{ color: '#8B5CF6' }} />
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#FFF' }}>Clicks by Source</span>
          </div>
          <HBarChart data={clicks?.bySource || []} maxVal={maxClickCount} />
        </div>

        <MediaCategoryBreakdown
          categoryBreakdown={categoryBreakdown}
          channelCategories={channelCategories}
        />
      </div>

      {/* Audience Content Interests */}
      <AudienceContentInterests byContent={byContent} />

      {/* Recent Peak Moments */}
      <div style={{ marginTop: '24px' }}>
      <div style={{
        background: '#111118', borderRadius: '14px', padding: '24px',
        border: '1px solid #1A1A2E',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <Flame size={18} style={{ color: '#F59E0B' }} />
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#FFF' }}>Recent Peak Moments</span>
        </div>
        {dashboard?.recentPeakMoments?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {dashboard.recentPeakMoments.map(m => (
              <div
                key={m._id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 12px', borderRadius: '10px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid transparent',
                  transition: 'all 0.15s', cursor: 'default',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.06)'; e.currentTarget.style.borderColor = '#1A1A2E'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.borderColor = 'transparent'; }}
              >
                {m.videoThumbnail ? (
                  <img src={m.videoThumbnail} alt="" style={{
                    width: 72, height: 40, borderRadius: '6px', objectFit: 'cover', flexShrink: 0,
                  }} />
                ) : (
                  <div style={{
                    width: 72, height: 40, borderRadius: '6px', flexShrink: 0,
                    background: '#1A1A2E', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Video size={16} style={{ color: '#333' }} />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px', color: '#E5E5E5', fontWeight: '500',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{m.title}</div>
                  <div style={{ fontSize: '11px', color: '#555', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{m.timestampFormatted}</span>
                    <span style={{ color: '#333' }}>|</span>
                    <span>{m.likes || 0} likes</span>
                    {m.isOfficialPick && (
                      <span style={{
                        color: '#F59E0B', fontSize: '10px', fontWeight: '600',
                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                      }}>
                        <Star size={10} fill="#F59E0B" /> Official
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#444', fontSize: '13px', textAlign: 'center', padding: '40px 20px' }}>
            No peak moments yet for your channel.
          </div>
        )}
      </div>
      </div>

      {/* ── Community & Appearance Stats ── */}
      <div style={{ marginTop: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>

        {/* Follows + Favorites */}
        <div style={{ background: '#111118', borderRadius: '14px', padding: '24px', border: '1px solid #1A1A2E' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' }}>Community Engagement</div>
          {[
            { label: 'Follows on ReactorHub', value: dashboard?.followCount   || 0, color: '#8B5CF6', icon: '👥' },
            { label: 'Favorited by users',    value: dashboard?.favoriteCount || 0, color: '#EC4899', icon: '❤️' },
            { label: 'Peak Moment likes',     value: dashboard?.peakMomentLikes || 0, color: '#F59E0B', icon: '🔥' },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #1A1A2E' }}>
              <span style={{ fontSize: '18px' }}>{s.icon}</span>
              <span style={{ flex: 1, fontSize: '13px', color: '#999' }}>{s.label}</span>
              <span style={{ fontSize: '18px', fontWeight: '800', color: s.color }}>{(s.value || 0).toLocaleString()}</span>
            </div>
          ))}
        </div>

        {/* Featured Carousel */}
        <div style={{ background: '#111118', borderRadius: '14px', padding: '24px', border: '1px solid #1A1A2E' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' }}>Featured Carousel</div>
          {dashboard?.carousel ? (
            <>
              <div style={{ fontSize: '36px', fontWeight: '800', color: dashboard.carousel.currentlyActive ? '#06B6D4' : '#FFF' }}>
                {dashboard.carousel.appearances}
              </div>
              <div style={{ fontSize: '12px', color: '#555', marginBottom: '12px' }}>times added to homepage carousel</div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: dashboard.carousel.currentlyActive ? '#06B6D4' : '#555' }}>
                {dashboard.carousel.currentlyActive ? '✓ Currently featured on homepage' : 'Not currently featured'}
              </div>
              {dashboard.carousel.firstAdded && (
                <div style={{ fontSize: '11px', color: '#444', marginTop: '8px' }}>
                  First featured: {new Date(dashboard.carousel.firstAdded).toLocaleDateString()}
                </div>
              )}
            </>
          ) : (
            <div style={{ color: '#444', fontSize: '13px', paddingTop: '8px' }}>Not yet featured in carousel</div>
          )}
        </div>

        {/* Reaction of the Week */}
        <div style={{ background: '#111118', borderRadius: '14px', padding: '24px', border: '1px solid #1A1A2E' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' }}>Reaction of the Week</div>
          {dashboard?.reactionOfWeek?.length > 0 ? (
            <>
              <div style={{ fontSize: '36px', fontWeight: '800', color: '#F59E0B' }}>🏆</div>
              <div style={{ fontSize: '14px', color: '#F59E0B', fontWeight: '700', marginTop: '8px', marginBottom: '4px' }}>Currently Featured!</div>
              {dashboard.reactionOfWeek.map(r => (
                <div key={r.slot} style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                  Slot #{r.slot} — {r.title || 'Reaction video'}
                </div>
              ))}
            </>
          ) : (
            <div style={{ color: '#444', fontSize: '13px', paddingTop: '8px' }}>Not currently featured as Reaction of the Week</div>
          )}
        </div>
      </div>

      {/* ── Poll History ── */}
      {(dashboard?.polls?.appearances > 0 || dashboard?.polls?.reactorRequestCount > 0) && (
        <div style={{ marginTop: '24px', background: '#111118', borderRadius: '14px', padding: '24px', border: '1px solid #1A1A2E' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <span style={{ fontSize: '18px' }}>🗳️</span>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#FFF' }}>Poll Appearances</span>
          </div>

          {/* Poll summary KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Poll appearances',      value: dashboard.polls.appearances,         color: '#8B5CF6' },
              { label: 'Votes received',         value: dashboard.polls.votesReceived,       color: '#F59E0B' },
              { label: 'Reactor requests',       value: dashboard.polls.reactorRequestCount, color: '#06B6D4' },
              { label: 'Reactor request votes',  value: dashboard.polls.reactorRequestVotes, color: '#EC4899' },
            ].map(s => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '14px', borderTop: `2px solid ${s.color}` }}>
                <div style={{ fontSize: '22px', fontWeight: '800', color: s.color }}>{(s.value || 0).toLocaleString()}</div>
                <div style={{ fontSize: '11px', color: '#555', marginTop: '4px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Poll history table */}
          {dashboard.polls.history?.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1A1A2E' }}>
                    {['Poll', 'Category', 'Your votes', 'Total votes', 'Share', 'Status', 'Date'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#555', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dashboard.polls.history.map((p, i) => {
                    const pct = p.totalPollVotes > 0 ? ((p.voteCount / p.totalPollVotes) * 100).toFixed(1) : '0.0';
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #1A1A2E' }}>
                        <td style={{ padding: '8px 10px', color: '#E5E5E5', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.pollTitle}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{ padding: '2px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', background: 'rgba(139,92,246,0.15)', color: '#A78BFA', textTransform: 'capitalize' }}>{p.category}</span>
                        </td>
                        <td style={{ padding: '8px 10px', fontWeight: '700', color: '#F59E0B' }}>{(p.voteCount || 0).toLocaleString()}</td>
                        <td style={{ padding: '8px 10px', color: '#555' }}>{(p.totalPollVotes || 0).toLocaleString()}</td>
                        <td style={{ padding: '8px 10px', fontWeight: '700', color: parseFloat(pct) >= 40 ? '#22C55E' : '#555' }}>{pct}%</td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{ fontSize: '10px', fontWeight: '700', color: p.isActive ? '#22C55E' : '#555' }}>{p.isActive ? 'Active' : 'Closed'}</span>
                        </td>
                        <td style={{ padding: '8px 10px', color: '#444', whiteSpace: 'nowrap', fontSize: '11px' }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default CreatorOverviewPanel;
